import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { init, useFocusable, FocusContext } from "@noriginmedia/norigin-spatial-navigation";
import { useEvents } from "./context/EventsContext";
import { getEPGFirstFocusKey, safeSetFocus } from "./utils/shared/focus";
import { EVENT_STATUS, EVENT_TITLES, SECTION_TITLES, SECTION_TYPES } from "./constants/event";
import { capitalizeFirstLetter } from "./utils/shared/string";
import EPGSectionRow from "./EPGSectionRow";
import EPGFilteredEventsRow from "./EPGFilteredSectionRow";
import sortEvents from './utils/shared/sortEvents';
import "./epg.scss";

init({
    debug: false,
    visualDebug: false,
});

const getRowsData = (liveEvents, channels, upcomingEvents, scheduledEvents, sportsData, leaguesData, partnersData) => {
    return [
        {
            render: !!partnersData.length,
            rowType: SECTION_TYPES.PARTNERS,
            rowId: "partners",
            title: EVENT_TITLES[SECTION_TYPES.PARTNERS],
            items: partnersData,
            virtualizationParams: {
                cardWidth: 168,
                cardGap: 24,
                amountOfCardsInWindow: 15,
            },
        },
        {
            render: !!liveEvents.length,
            rowType: SECTION_TYPES.EVENTS,
            rowId: "live",
            title: EVENT_TITLES[EVENT_STATUS.LIVE],
            items: liveEvents,
            virtualizationParams: {}, // We use the default params for the event rows
        },
        {
            render: !!sportsData.length,
            rowType: SECTION_TYPES.SPORTS,
            rowId: "sports",
            title: EVENT_TITLES[SECTION_TYPES.SPORTS],
            items: sportsData,
            virtualizationParams: {
                cardWidth: 168,
                cardGap: 24,
                amountOfCardsInWindow: 15,
            },
        },
        {
            render: !!leaguesData.length,
            rowType: SECTION_TYPES.LEAGUES,
            rowId: "leagues",
            title: EVENT_TITLES[SECTION_TYPES.LEAGUES],
            items: leaguesData,
            virtualizationParams: {
                cardWidth: 168,
                cardGap: 24,
                amountOfCardsInWindow: 15,
            },
        },
        {
            render: !!channels.length,
            rowType: SECTION_TYPES.EVENTS,
            rowId: "channels",
            title: SECTION_TITLES.CHANNELS,
            items: channels,
            virtualizationParams: {},
        },
        {
            render: !!upcomingEvents.length,
            rowType: SECTION_TYPES.EVENTS,
            rowId: "upcoming",
            title: EVENT_TITLES[EVENT_STATUS.COMING],
            items: upcomingEvents,
            virtualizationParams: {},
        },
        {
            render: !!scheduledEvents.length,
            rowType: SECTION_TYPES.EVENTS,
            rowId: "scheduled",
            title: EVENT_TITLES[EVENT_STATUS.SCHEDULED],
            items: scheduledEvents,
            virtualizationParams: {},
        },
    ];
};

const EPGContent = ({ sportsData, leaguesData, partnersData, onEnterPress, deeplinkEvent, shouldApplyComcastStyles, onWatchNow }) => {
    const { liveEvents, upcomingEvents, scheduledEvents, channels, showFiltered, selectedSport, handleFilterSelect, filteredEvents: getFilteredEvents, activeLiveContent } = useEvents();

    const enrichedPartners = useMemo(() => {
        return partnersData.map((partner) => {
            const matchingChannel = channels.find(
                (ch) => ch.subscriptionType?.backwards_compatible_name === partner.searchTerm
            );
            if (!matchingChannel) return partner;
            return {
                ...partner,
                channelData: {
                    bannerUrl: matchingChannel.subscriptionType?.banner_url,
                    channelName: matchingChannel.title,
                    channelId: matchingChannel.id,
                },
            };
        });
    }, [partnersData, channels]);

    const sortedLiveEvents = useMemo(() => {
        return sortEvents(liveEvents, activeLiveContent);
    }, [liveEvents, activeLiveContent]);

    const { ref, focusKey } = useFocusable({
        focusKey: "epg-content-container",
    });

    const rowsData = useMemo(
        () => getRowsData(sortedLiveEvents, channels, upcomingEvents, scheduledEvents, sportsData, leaguesData, enrichedPartners),
        [sortedLiveEvents, channels, upcomingEvents, scheduledEvents, sportsData, leaguesData, enrichedPartners]
    );


    // When entering filtered view, reset scroll to top so the banner/title is visible
    // (scrollingRows retains its scroll position from the main view otherwise)
    const scrollingRowsRef = useRef(null);

    useEffect(() => {
        if (showFiltered && scrollingRowsRef.current) {
            scrollingRowsRef.current.scrollTop = 0;
        }
    }, [showFiltered]);

    // Focus management
    useEffect(() => {
        if (!showFiltered && !deeplinkEvent) {
            const focusKey = getEPGFirstFocusKey(rowsData);
            // When returning to main view, focus on first available element
            setTimeout(() => {
                safeSetFocus(focusKey);
            }, 50);
        }
    }, [showFiltered]);

    const handleEnterPress = useCallback(
        (focusProps, rowType) => {
            const { focusKey } = focusProps;

            switch (rowType) {
                case SECTION_TYPES.SPORTS:
                    const sport = sportsData.find((s) => String(s.id) === String(focusKey));
                    if (sport) handleFilterSelect(sport, { type: "sport" });
                    break;
                case SECTION_TYPES.LEAGUES:
                    const league = leaguesData.find((l) => String(l.id) === String(focusKey));
                    if (league) handleFilterSelect(league, { type: "league" });
                    break;
                case SECTION_TYPES.PARTNERS:
                    const partner = enrichedPartners.find((p) => String(p.id) === String(focusKey));
                    if (partner) handleFilterSelect(partner, { type: "partner" });
                    break;
                default:
                    onEnterPress(focusProps);
            }
        },
        [onEnterPress, sportsData, leaguesData, enrichedPartners, handleFilterSelect]
    );

    const renderMainContent = () => {
        return (
            <div>
                {rowsData
                    .filter((row) => row.render)
                    .map((row, index) => (
                        <EPGSectionRow
                            key={`${row.rowType}-${index}`}
                            rowType={row.rowType}
                            rowId={row.rowId}
                            title={row.title}
                            items={row.items}
                            virtualizationParams={row.virtualizationParams}
                            onEnterPress={(focusProps) => handleEnterPress(focusProps, row.rowType)}
                        />
                    ))}
            </div>
        );
    };

    // Render the filtered events
    const renderFilteredContent = () => {
        const channelData = selectedSport?.channelData || null;
        const rawTitle = selectedSport?.name || selectedSport?.alias || "Filtered Events";
        const title = channelData ? "Live and Upcoming Events" : `${capitalizeFirstLetter(rawTitle)} Events`;
        const filteredEvents = getFilteredEvents();

        return (
            <EPGFilteredEventsRow
                events={filteredEvents}
                title={title}
                onEnterPress={onEnterPress}
                shouldApplyComcastStyles={shouldApplyComcastStyles}
                channelData={channelData}
                onWatchNow={onWatchNow}
            />
        );
    };

    return (
        <FocusContext.Provider value={focusKey}>
            <div ref={ref} className="containerWrapper">
                <div ref={scrollingRowsRef} className="scrollingRows">{showFiltered ? renderFilteredContent() : renderMainContent()}</div>
            </div>
        </FocusContext.Provider>
    );
};
export default EPGContent;
